import { useState, useEffect } from 'react';
import { XMarkIcon, PlusIcon, TrashIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import I18nField from './I18nField';
import { createI18nObject } from '../utils/i18n';

const SURCHARGE_TYPES = ['OVERSIZE', 'OVERWEIGHT', 'REMOTE_AREA', 'RESIDENTIAL'];

const emptyTier = (minWeight = 0) => ({
  name: '',
  minWeightGrams: minWeight,
  maxWeightGrams: minWeight + 1000,
  priceInCents: 500,
  zoneOverrides: [],
});

export default function ShippingMethodForm({ method, onSave, onCancel }) {
  const { user } = useAuth();
  const supportedLanguages = user?.supportedLanguages || ['pt'];

  const [formData, setFormData] = useState({
    carrierName: '',
    key: '',
    name: createI18nObject(supportedLanguages),
    description: createI18nObject(supportedLanguages),
    serviceLevel: '',
    estimatedDeliveryDays: { min: 1, max: 3 },
    dimWeightDivisor: 5000,
    maxWeightGrams: '',
    maxDimensions: { maxLength: '', maxWidth: '', maxHeight: '', maxGirth: '' },
    pricingTiers: [emptyTier()],
    surcharges: [],
    fuelSurchargePercent: 0,
    pickupFeeInCents: 0,
    countries: ['PT'],
    freeShippingThresholdInCents: 0,
    displayOrder: 0,
    isDefault: false,
    internalNotes: '',
    trackingUrlTemplate: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [countryInput, setCountryInput] = useState('');
  const [showMaxDimensions, setShowMaxDimensions] = useState(false);

  useEffect(() => {
    if (method) {
      const hasMaxDims = method.maxDimensions &&
        Object.values(method.maxDimensions).some(v => v != null && v !== 0);

      setFormData({
        carrierName: method.carrierName || '',
        key: method.key || '',
        name: method.name || createI18nObject(supportedLanguages),
        description: method.description || createI18nObject(supportedLanguages),
        serviceLevel: method.serviceLevel || '',
        estimatedDeliveryDays: method.estimatedDeliveryDays || { min: 1, max: 3 },
        dimWeightDivisor: method.dimWeightDivisor || 5000,
        maxWeightGrams: method.maxWeightGrams ?? '',
        maxDimensions: {
          maxLength: method.maxDimensions?.maxLength ?? '',
          maxWidth: method.maxDimensions?.maxWidth ?? '',
          maxHeight: method.maxDimensions?.maxHeight ?? '',
          maxGirth: method.maxDimensions?.maxGirth ?? '',
        },
        pricingTiers: method.pricingTiers?.length
          ? method.pricingTiers.map(t => ({
              name: t.name || '',
              minWeightGrams: t.minWeightGrams ?? 0,
              maxWeightGrams: t.maxWeightGrams ?? 1000,
              priceInCents: t.priceInCents ?? 0,
              zoneOverrides: t.zoneOverrides || [],
            }))
          : [emptyTier()],
        surcharges: method.surcharges || [],
        fuelSurchargePercent: method.fuelSurchargePercent || 0,
        pickupFeeInCents: method.pickupFeeInCents || 0,
        countries: method.countries || ['PT'],
        freeShippingThresholdInCents: method.freeShippingThresholdInCents || 0,
        displayOrder: method.displayOrder || 0,
        isDefault: method.isDefault || false,
        internalNotes: method.internalNotes || '',
        trackingUrlTemplate: method.trackingUrlTemplate || '',
      });

      if (hasMaxDims) setShowMaxDimensions(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- init form when prop changes
  }, [method]);

  // ─── Submit ──────────────────────────────────────────────────────────────────

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (formData.pricingTiers.length === 0) {
      setError('At least one pricing tier is required');
      setLoading(false);
      return;
    }

    // Build payload — convert empty strings to null for optional numeric fields
    const toNullableInt = (v) => (v === '' || v === null || v === undefined ? null : Number(v));

    const maxDimensions = showMaxDimensions
      ? {
          maxLength: toNullableInt(formData.maxDimensions.maxLength),
          maxWidth: toNullableInt(formData.maxDimensions.maxWidth),
          maxHeight: toNullableInt(formData.maxDimensions.maxHeight),
          maxGirth: toNullableInt(formData.maxDimensions.maxGirth),
        }
      : null;

    const payload = {
      ...formData,
      maxWeightGrams: toNullableInt(formData.maxWeightGrams),
      maxDimensions,
    };

    try {
      await onSave(payload);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save shipping method');
    } finally {
      setLoading(false);
    }
  };

  // ─── Generic handlers ────────────────────────────────────────────────────────

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : type === 'number' ? Number(value) : value,
    });
  };

  const handleI18nChange = (field, lang, value) => {
    setFormData({ ...formData, [field]: { ...formData[field], [lang]: value } });
  };

  const handleDeliveryDaysChange = (field, value) => {
    setFormData({
      ...formData,
      estimatedDeliveryDays: { ...formData.estimatedDeliveryDays, [field]: Number(value) },
    });
  };

  const handleMaxDimensionChange = (field, value) => {
    setFormData({
      ...formData,
      maxDimensions: { ...formData.maxDimensions, [field]: value === '' ? '' : Number(value) },
    });
  };

  // ─── Pricing Tiers ──────────────────────────────────────────────────────────

  const addPricingTier = () => {
    const lastTier = formData.pricingTiers[formData.pricingTiers.length - 1];
    const minWeight = lastTier ? lastTier.maxWeightGrams + 1 : 0;
    setFormData({
      ...formData,
      pricingTiers: [...formData.pricingTiers, emptyTier(minWeight)],
    });
  };

  const removePricingTier = (index) => {
    setFormData({
      ...formData,
      pricingTiers: formData.pricingTiers.filter((_, i) => i !== index),
    });
  };

  const updatePricingTier = (index, field, value) => {
    const updated = [...formData.pricingTiers];
    updated[index] = {
      ...updated[index],
      [field]: field === 'name' ? value : Number(value),
    };
    setFormData({ ...formData, pricingTiers: updated });
  };

  // ─── Zone Overrides ─────────────────────────────────────────────────────────

  const addZoneOverride = (tierIndex) => {
    const updated = [...formData.pricingTiers];
    updated[tierIndex] = {
      ...updated[tierIndex],
      zoneOverrides: [...updated[tierIndex].zoneOverrides, { countryCode: '', priceInCents: 0 }],
    };
    setFormData({ ...formData, pricingTiers: updated });
  };

  const removeZoneOverride = (tierIndex, overrideIndex) => {
    const updated = [...formData.pricingTiers];
    updated[tierIndex] = {
      ...updated[tierIndex],
      zoneOverrides: updated[tierIndex].zoneOverrides.filter((_, i) => i !== overrideIndex),
    };
    setFormData({ ...formData, pricingTiers: updated });
  };

  const updateZoneOverride = (tierIndex, overrideIndex, field, value) => {
    const updated = [...formData.pricingTiers];
    const overrides = [...updated[tierIndex].zoneOverrides];
    overrides[overrideIndex] = {
      ...overrides[overrideIndex],
      [field]: field === 'countryCode' ? value.toUpperCase() : Number(value),
    };
    updated[tierIndex] = { ...updated[tierIndex], zoneOverrides: overrides };
    setFormData({ ...formData, pricingTiers: updated });
  };

  // ─── Surcharges ──────────────────────────────────────────────────────────────

  const addSurcharge = () => {
    setFormData({
      ...formData,
      surcharges: [...formData.surcharges, { type: 'OVERWEIGHT', condition: '', amountInCents: 0 }],
    });
  };

  const removeSurcharge = (index) => {
    setFormData({
      ...formData,
      surcharges: formData.surcharges.filter((_, i) => i !== index),
    });
  };

  const updateSurcharge = (index, field, value) => {
    const updated = [...formData.surcharges];
    updated[index] = {
      ...updated[index],
      [field]: field === 'amountInCents' ? Number(value) : value,
    };
    setFormData({ ...formData, surcharges: updated });
  };

  // ─── Countries ───────────────────────────────────────────────────────────────

  const addCountry = () => {
    const code = countryInput.trim().toUpperCase();
    if (code.length === 2 && !formData.countries.includes(code)) {
      setFormData({ ...formData, countries: [...formData.countries, code] });
      setCountryInput('');
    }
  };

  const removeCountry = (index) => {
    setFormData({
      ...formData,
      countries: formData.countries.filter((_, i) => i !== index),
    });
  };

  // ─── Render ──────────────────────────────────────────────────────────────────

  const inputClass =
    'w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-100';
  const inputSmClass =
    'w-full px-2 py-1 text-sm border border-slate-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent';

  return (
    <div className="fixed inset-0 bg-slate-900 bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50">
          <h2 className="text-2xl font-bold text-slate-900">
            {method ? 'Edit Shipping Method' : 'Add Shipping Method'}
          </h2>
          <button onClick={onCancel} className="text-slate-400 hover:text-slate-600 transition-colors">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="px-6 py-6 space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            {/* Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="carrierName" className="block text-sm font-medium text-slate-700 mb-2">
                  Carrier Name *
                </label>
                <input
                  type="text" id="carrierName" name="carrierName"
                  value={formData.carrierName} onChange={handleChange}
                  required disabled={loading} maxLength={100}
                  placeholder="e.g., CTT Expresso" className={inputClass}
                />
              </div>
              <div>
                <label htmlFor="key" className="block text-sm font-medium text-slate-700 mb-2">
                  Key *
                </label>
                <input
                  type="text" id="key" name="key"
                  value={formData.key} onChange={handleChange}
                  required disabled={loading} maxLength={40}
                  placeholder="e.g., ctt-standard" className={inputClass}
                />
              </div>
            </div>

            {/* Name i18n */}
            <I18nField
              label="Name" value={formData.name}
              onChange={(lang, value) => handleI18nChange('name', lang, value)}
              languages={supportedLanguages} required disabled={loading} maxLength={100}
            />

            {/* Description i18n */}
            <I18nField
              label="Description" value={formData.description}
              onChange={(lang, value) => handleI18nChange('description', lang, value)}
              languages={supportedLanguages} type="textarea" disabled={loading}
              rows={2} maxLength={500}
            />

            {/* Service Level and Delivery Days */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label htmlFor="serviceLevel" className="block text-sm font-medium text-slate-700 mb-2">
                  Service Level *
                </label>
                <input
                  type="text" id="serviceLevel" name="serviceLevel"
                  value={formData.serviceLevel} onChange={handleChange}
                  required disabled={loading} maxLength={50}
                  placeholder="e.g., 2-3 dias" className={inputClass}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Min Days *</label>
                <input
                  type="number" value={formData.estimatedDeliveryDays.min}
                  onChange={(e) => handleDeliveryDaysChange('min', e.target.value)}
                  required disabled={loading} min={0} className={inputClass}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Max Days *</label>
                <input
                  type="number" value={formData.estimatedDeliveryDays.max}
                  onChange={(e) => handleDeliveryDaysChange('max', e.target.value)}
                  required disabled={loading} min={formData.estimatedDeliveryDays.min}
                  className={inputClass}
                />
              </div>
            </div>

            {/* Carrier Settings */}
            <div className="border border-slate-200 rounded-lg p-4 bg-slate-50">
              <h3 className="text-sm font-semibold text-slate-800 mb-4">Carrier Settings</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">
                    DIM Weight Divisor
                  </label>
                  <input
                    type="number" name="dimWeightDivisor"
                    value={formData.dimWeightDivisor} onChange={handleChange}
                    disabled={loading} min={1} className={inputSmClass}
                  />
                  <p className="mt-1 text-xs text-slate-500">Default: 5000</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">
                    Max Weight (g)
                  </label>
                  <input
                    type="number" name="maxWeightGrams"
                    value={formData.maxWeightGrams}
                    onChange={(e) =>
                      setFormData({ ...formData, maxWeightGrams: e.target.value === '' ? '' : Number(e.target.value) })
                    }
                    disabled={loading} min={1} placeholder="Unlimited"
                    className={inputSmClass}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">
                    Fuel Surcharge %
                  </label>
                  <input
                    type="number" name="fuelSurchargePercent"
                    value={formData.fuelSurchargePercent} onChange={handleChange}
                    disabled={loading} min={0} max={100} step={0.1}
                    className={inputSmClass}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">
                    Pickup Fee (cents)
                  </label>
                  <input
                    type="number" name="pickupFeeInCents"
                    value={formData.pickupFeeInCents} onChange={handleChange}
                    disabled={loading} min={0} className={inputSmClass}
                  />
                  {formData.pickupFeeInCents > 0 && (
                    <span className="text-xs text-slate-500">
                      {'\u20AC'}{(formData.pickupFeeInCents / 100).toFixed(2)}
                    </span>
                  )}
                </div>
              </div>

              {/* Tracking URL Template */}
              <div className="mt-4">
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  Tracking URL Template
                </label>
                <input
                  type="text" name="trackingUrlTemplate"
                  value={formData.trackingUrlTemplate} onChange={handleChange}
                  disabled={loading} maxLength={500}
                  placeholder="e.g., https://ctt.pt/t/{{TRACKING_CODE}}"
                  className={inputSmClass}
                />
                <p className="mt-1 text-xs text-slate-500">
                  Use <code className="bg-slate-200 px-1 rounded">{'{{TRACKING_CODE}}'}</code> as placeholder for the tracking number
                </p>
              </div>

              {/* Max Dimensions (collapsible) */}
              <div className="mt-4">
                <button
                  type="button"
                  onClick={() => setShowMaxDimensions(!showMaxDimensions)}
                  className="inline-flex items-center text-sm text-slate-600 hover:text-slate-800"
                >
                  {showMaxDimensions ? (
                    <ChevronUpIcon className="h-4 w-4 mr-1" />
                  ) : (
                    <ChevronDownIcon className="h-4 w-4 mr-1" />
                  )}
                  Max Dimensions (mm)
                </button>
                {showMaxDimensions && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-2">
                    {['maxLength', 'maxWidth', 'maxHeight', 'maxGirth'].map((field) => (
                      <div key={field}>
                        <label className="block text-xs text-slate-600 mb-1">
                          {field.replace('max', '')}
                        </label>
                        <input
                          type="number"
                          value={formData.maxDimensions[field]}
                          onChange={(e) => handleMaxDimensionChange(field, e.target.value)}
                          disabled={loading} min={1} placeholder="No limit"
                          className={inputSmClass}
                        />
                      </div>
                    ))}
                    <p className="col-span-full text-xs text-slate-500">
                      Girth = 2 x (Width + Height) + Length. Leave empty for no limit.
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Pricing Tiers */}
            <div>
              <div className="flex justify-between items-center mb-3">
                <label className="block text-sm font-medium text-slate-700">Pricing Tiers *</label>
                <button
                  type="button" onClick={addPricingTier}
                  className="inline-flex items-center px-3 py-1.5 text-sm bg-green-100 hover:bg-green-200 text-green-700 rounded-lg transition-colors"
                >
                  <PlusIcon className="h-4 w-4 mr-1" />
                  Add Tier
                </button>
              </div>

              {formData.pricingTiers.length === 0 ? (
                <div className="text-center py-8 bg-slate-50 rounded-lg border-2 border-dashed border-slate-300">
                  <p className="text-slate-500">No pricing tiers defined. Add at least one.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {formData.pricingTiers.map((tier, tierIndex) => (
                    <div key={tierIndex} className="border border-slate-200 rounded-lg p-4 bg-slate-50">
                      <div className="flex justify-between items-start mb-3">
                        <span className="text-sm font-medium text-slate-600">Tier {tierIndex + 1}</span>
                        {formData.pricingTiers.length > 1 && (
                          <button
                            type="button" onClick={() => removePricingTier(tierIndex)}
                            className="text-red-600 hover:text-red-800 p-1"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        )}
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div>
                          <label className="block text-xs text-slate-600 mb-1">Name</label>
                          <input
                            type="text" value={tier.name}
                            onChange={(e) => updatePricingTier(tierIndex, 'name', e.target.value)}
                            maxLength={50} placeholder="e.g., Light"
                            className={inputSmClass}
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-slate-600 mb-1">Min Weight (g) *</label>
                          <input
                            type="number" value={tier.minWeightGrams}
                            onChange={(e) => updatePricingTier(tierIndex, 'minWeightGrams', e.target.value)}
                            required min={0} className={inputSmClass}
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-slate-600 mb-1">Max Weight (g) *</label>
                          <input
                            type="number" value={tier.maxWeightGrams}
                            onChange={(e) => updatePricingTier(tierIndex, 'maxWeightGrams', e.target.value)}
                            required min={1} className={inputSmClass}
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-slate-600 mb-1">Price (cents) *</label>
                          <input
                            type="number" value={tier.priceInCents}
                            onChange={(e) => updatePricingTier(tierIndex, 'priceInCents', e.target.value)}
                            required min={0} className={inputSmClass}
                          />
                          <span className="text-xs text-slate-500">
                            {'\u20AC'}{(tier.priceInCents / 100).toFixed(2)}
                          </span>
                        </div>
                      </div>

                      {/* Zone Overrides */}
                      <div className="mt-3 pt-3 border-t border-slate-200">
                        <div className="flex justify-between items-center mb-2">
                          <label className="text-xs font-medium text-slate-600">Zone Overrides</label>
                          <button
                            type="button" onClick={() => addZoneOverride(tierIndex)}
                            className="inline-flex items-center px-2 py-0.5 text-xs bg-blue-100 hover:bg-blue-200 text-blue-700 rounded transition-colors"
                          >
                            <PlusIcon className="h-3 w-3 mr-1" />
                            Zone
                          </button>
                        </div>
                        {tier.zoneOverrides.length === 0 ? (
                          <p className="text-xs text-slate-400">No zone overrides</p>
                        ) : (
                          <div className="space-y-1">
                            {tier.zoneOverrides.map((override, oIndex) => (
                              <div key={oIndex} className="flex items-center gap-2 bg-white p-2 rounded border border-slate-200">
                                <div className="w-20">
                                  <input
                                    type="text" value={override.countryCode}
                                    onChange={(e) => updateZoneOverride(tierIndex, oIndex, 'countryCode', e.target.value)}
                                    maxLength={2} placeholder="CC"
                                    className="w-full px-2 py-1 text-sm border border-slate-300 rounded text-center uppercase"
                                  />
                                </div>
                                <div className="flex-1">
                                  <input
                                    type="number" value={override.priceInCents}
                                    onChange={(e) => updateZoneOverride(tierIndex, oIndex, 'priceInCents', e.target.value)}
                                    min={0} placeholder="Price (cents)"
                                    className={inputSmClass}
                                  />
                                </div>
                                <span className="text-xs text-slate-500 w-16">
                                  {'\u20AC'}{(override.priceInCents / 100).toFixed(2)}
                                </span>
                                <button
                                  type="button" onClick={() => removeZoneOverride(tierIndex, oIndex)}
                                  className="text-red-500 hover:text-red-700 p-1"
                                >
                                  <TrashIcon className="h-3 w-3" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Surcharges */}
            <div>
              <div className="flex justify-between items-center mb-3">
                <label className="block text-sm font-medium text-slate-700">Surcharges</label>
                <button
                  type="button" onClick={addSurcharge}
                  className="inline-flex items-center px-3 py-1.5 text-sm bg-amber-100 hover:bg-amber-200 text-amber-700 rounded-lg transition-colors"
                >
                  <PlusIcon className="h-4 w-4 mr-1" />
                  Add Surcharge
                </button>
              </div>

              {formData.surcharges.length === 0 ? (
                <p className="text-xs text-slate-400">No surcharges defined</p>
              ) : (
                <div className="space-y-2">
                  {formData.surcharges.map((surcharge, sIndex) => (
                    <div key={sIndex} className="flex items-center gap-3 bg-slate-50 p-3 rounded-lg border border-slate-200">
                      <div className="w-40">
                        <select
                          value={surcharge.type}
                          onChange={(e) => updateSurcharge(sIndex, 'type', e.target.value)}
                          className="w-full px-2 py-1 text-sm border border-slate-300 rounded bg-white"
                        >
                          {SURCHARGE_TYPES.map((t) => (
                            <option key={t} value={t}>{t.replace('_', ' ')}</option>
                          ))}
                        </select>
                      </div>
                      <div className="flex-1">
                        <input
                          type="text" value={surcharge.condition}
                          onChange={(e) => updateSurcharge(sIndex, 'condition', e.target.value)}
                          maxLength={200} placeholder="Condition (optional)"
                          className={inputSmClass}
                        />
                      </div>
                      <div className="w-28">
                        <input
                          type="number" value={surcharge.amountInCents}
                          onChange={(e) => updateSurcharge(sIndex, 'amountInCents', e.target.value)}
                          min={0} placeholder="Amount"
                          className={inputSmClass}
                        />
                      </div>
                      <span className="text-xs text-slate-500 w-14">
                        {'\u20AC'}{(surcharge.amountInCents / 100).toFixed(2)}
                      </span>
                      <button
                        type="button" onClick={() => removeSurcharge(sIndex)}
                        className="text-red-500 hover:text-red-700 p-1"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Countries */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Countries</label>
              <div className="flex flex-wrap gap-2 mb-2">
                {formData.countries.map((country, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full"
                  >
                    {country}
                    <button
                      type="button" onClick={() => removeCountry(index)}
                      className="hover:bg-blue-200 rounded-full p-0.5"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  type="text" value={countryInput}
                  onChange={(e) => setCountryInput(e.target.value.toUpperCase())}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addCountry())}
                  maxLength={2} placeholder="Country code (e.g., PT)"
                  className="w-32 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <button
                  type="button" onClick={addCountry}
                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition-colors"
                >
                  Add
                </button>
              </div>
              <p className="mt-1 text-xs text-slate-500">ISO 3166-1 alpha-2 country codes</p>
            </div>

            {/* Free Shipping and Display Order */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="freeShippingThresholdInCents" className="block text-sm font-medium text-slate-700 mb-2">
                  Free Shipping Threshold (cents)
                </label>
                <input
                  type="number" id="freeShippingThresholdInCents" name="freeShippingThresholdInCents"
                  value={formData.freeShippingThresholdInCents} onChange={handleChange}
                  disabled={loading} min={0} className={inputClass}
                />
                <p className="mt-1 text-xs text-slate-500">
                  {formData.freeShippingThresholdInCents > 0
                    ? `Free shipping for orders over \u20AC${(formData.freeShippingThresholdInCents / 100).toFixed(2)}`
                    : 'Set to 0 to disable free shipping'}
                </p>
              </div>
              <div>
                <label htmlFor="displayOrder" className="block text-sm font-medium text-slate-700 mb-2">
                  Display Order
                </label>
                <input
                  type="number" id="displayOrder" name="displayOrder"
                  value={formData.displayOrder} onChange={handleChange}
                  disabled={loading} min={0} className={inputClass}
                />
              </div>
            </div>

            {/* Internal Notes */}
            <div>
              <label htmlFor="internalNotes" className="block text-sm font-medium text-slate-700 mb-2">
                Internal Notes
              </label>
              <textarea
                id="internalNotes" name="internalNotes"
                value={formData.internalNotes} onChange={handleChange}
                disabled={loading} rows={3} maxLength={500}
                placeholder="Optional internal notes..."
                className={`${inputClass} resize-none`}
              />
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200 bg-slate-50">
            <button
              type="button" onClick={onCancel} disabled={loading}
              className="px-5 py-2.5 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit" disabled={loading}
              className="px-5 py-2.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50 transition-colors shadow-sm"
            >
              {loading ? 'Saving...' : 'Save Shipping Method'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
